// BookTickets/stages/DateAndTime/BookingList.tsx
'use client'

import { useCallback, useMemo } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { isFuture, format as formatDate } from 'date-fns'
import { enGB, es, fr, pt } from 'date-fns/locale'
import { Locale } from 'date-fns'
import { type LocaleKey } from '@/src/i18n/routing'

import styles from './index.module.scss'
import shared from '@booking/index.module.scss'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { Session } from '@booking/types/booking'

import { calcSessionTotal, ticketCount } from '@booking/lib/cart'
import { formatPrice } from '@booking/lib/money'

import trackGA4 from '@booking/services/trackingGa4'
import { format as formatInLisbonTz } from '@booking/lib/date'
import { useBookingExternal } from '@/src/components/BookTickets/state/external.context'

type BookingListProps = {
  onBook: (session: Session) => void
  date: Date
  minDate?: Date
  maxDate?: Date
  loading: boolean
  /** Fetching the day's sessions failed; shown with a retry instead of "no sessions". */
  error?: boolean
  sessions: Session[]
  onRetry?: () => void
  /**
   * Recommended: let the parent own date changes.
   * If you don’t pass it, arrows will be disabled (instead of console.warn + doing nothing).
   */
  onChangeDate?: (nextDate: Date) => void
  /**
   * 'grid' is for a flow that sells a single day and shows no calendar: the date
   * is written out in full and the times fill the width as a grid.
   */
  variant?: 'list' | 'grid'
}

function dateFnsLocale(locale: LocaleKey) {
  const map: Record<LocaleKey, Locale> = { en: enGB, pt: pt, fr, es }
  return map[locale as LocaleKey] ?? enGB
}

function getDateFormat(locale: LocaleKey) {
  return locale === 'pt' ? "dd 'de' MMM" : 'EEE dd MMM'
}

function getLongDateFormat(locale: LocaleKey, date: Date) {
  // French writes the first of the month as "1er".
  if (locale === 'fr') return date.getDate() === 1 ? "EEEE d'er' MMMM" : 'EEEE d MMMM'
  return locale === 'en' ? 'EEEE d MMMM' : "EEEE, d 'de' MMMM"
}

export default function BookingList({
  onBook,
  date,
  minDate,
  //maxDate, // unused for now, kept for parity
  loading,
  error = false,
  sessions,
  onRetry,
  onChangeDate,
  variant = 'list',
}: BookingListProps) {
  const t = useTranslations('booking')
  const { locale } = useBookingExternal()

  const state = useBookingState()
  const { dispatch } = useBookingActionsCtx()

  const formattedHeaderDate = useMemo(() => {
    return formatDate(date, getDateFormat(locale), { locale: dateFnsLocale(locale) })
  }, [date, locale])

  const isMin = useCallback((d: Date) => Boolean(minDate && d <= minDate), [minDate])

  const canGoPrev = useMemo(() => {
    return Boolean(onChangeDate) && isFuture(date) && !isMin(date)
  }, [date, isMin, onChangeDate])

  const canGoNext = useMemo(() => {
    // If maxDate is ever needed, include it here
    return Boolean(onChangeDate)
  }, [onChangeDate])

  const previousDay = useCallback(() => {
    if (!onChangeDate) return
    dispatch({ type: 'SET_SESSION', session: null })
    onChangeDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1))
  }, [date, dispatch, onChangeDate])

  const nextDay = useCallback(() => {
    if (!onChangeDate) return
    dispatch({ type: 'SET_SESSION', session: null })
    onChangeDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1))
  }, [date, dispatch, onChangeDate])

  const onSelect = useCallback(
    (session: Session) => {
      onBook(session)
      dispatch({ type: 'SET_SESSION', session })
      trackGA4.selectSession?.(session)
    },
    [dispatch, onBook],
  )

  const showNoneText = t('ticket.selectDate.unavailable')

  // Shown when there's nothing to list. A failed or unfinished fetch says so,
  // rather than looking like a day without sessions.
  const status = error ? (
    <div className={cn(styles.none)} role="alert">
      <p>{t('ticket.selectDate.loadError')}</p>
      <button type="button" className={cn(shared.button, styles.retry)} onClick={onRetry}>
        {t('ticket.selectDate.retry')}
      </button>
    </div>
  ) : loading ? (
    <div className={cn(styles.status)}>{t('loading')}</div>
  ) : (
    <div className={cn(styles.none)}>{showNoneText}</div>
  )

  const timeSlots = sessions.map((item) => (
    <TimeSlot
      key={item.id}
      session={item}
      onSelect={onSelect}
      selected={Boolean(state.session && state.session.id === item.id)}
      locale={locale}
    />
  ))

  if (variant === 'grid') {
    return (
      <div>
        <p className={cn(styles.gridDate)}>
          {formatDate(date, getLongDateFormat(locale, date), { locale: dateFnsLocale(locale) })}
        </p>
        {sessions.length ? (
          <div className={cn(styles.grid)} data-id="booking-list">
            {timeSlots}
          </div>
        ) : (
          status
        )}
      </div>
    )
  }

  return (
    <div className={cn(styles.wrapper, false && styles.special)}>
      <div className={cn(styles.header)}>
        <button
          type="button"
          className={cn(styles.arrow, styles.leftArrow, !canGoPrev && styles.disabled)}
          onClick={previousDay}
          disabled={!canGoPrev}
          aria-label={t('ticket.selectDate.previousDay', { default: 'Previous day' })}
        >
          ‹
        </button>

        <span>{formattedHeaderDate}</span>

        <button
          type="button"
          className={cn(styles.arrow, styles.rightArrow, !canGoNext && styles.disabled)}
          onClick={nextDay}
          disabled={!canGoNext}
          aria-label={t('ticket.selectDate.nextDay', { default: 'Next day' })}
        >
          ›
        </button>
      </div>

      {sessions.length ? (
        <div className={cn(styles.listWrapper)} data-id="booking-list">
          <div className={cn(styles.list, styles.anim)}>{timeSlots}</div>
        </div>
      ) : (
        status
      )}
    </div>
  )
}

type Availability = 'full' | 'amount' | 'open'

type TimeSlotProps = {
  session: Session
  selected: boolean
  onSelect: (session: Session) => void
  locale: string
}

function TimeSlot({ session, selected, onSelect, locale }: TimeSlotProps) {
  const t = useTranslations('booking')
  const state = useBookingState()

  const count = useMemo(() => ticketCount(state.quantities), [state.quantities])

  // The API refused to create a cart for this session, which is the only
  // reliable availability signal we get - it outranks the listed seat count.
  const refusedByApi = state.unavailableSessionIds.includes(session.id)

  const availability: Availability = useMemo(() => {
    if (refusedByApi) return 'full'
    const availableSeats = session.vacancies
    if (availableSeats === 0) return 'full'
    if (availableSeats == null) return 'open'
    if (availableSeats < count) return 'amount'
    return 'open'
  }, [count, refusedByApi, session.vacancies])

  const isDisabled = availability === 'full' || availability === 'amount'

  const unavailableText = useMemo(() => {
    if (availability === 'full') return t('ticket.selectDate.soldOut')
    if (availability === 'amount') return t('ticket.selectDate.soldOut')
    return ''
  }, [availability, t])

  const timeText = useMemo(() => {
    return formatInLisbonTz(new Date(session.dateTime), locale === 'en' ? 'HH:mm aaa' : 'HH:mm')
  }, [locale, session.dateTime])

  const timeTextBasic = useMemo(() => {
    return formatInLisbonTz(new Date(session.dateTime), 'HH:mm')
  }, [session.dateTime])

  const totalText = useMemo(() => {
    return formatPrice(calcSessionTotal({ session: session, quantities: state.quantities }))
  }, [state.quantities, session])

  const handleClick = useCallback(() => onSelect(session), [onSelect, session])

  if (isDisabled) {
    return (
      <div className={cn(styles.slot, styles.disabled)} data-time={timeTextBasic}>
        <div className={cn(styles.time)}>{timeText}</div>
        <div className={cn(styles.text)}>{unavailableText}</div>
      </div>
    )
  }

  return (
    <button
      type="button"
      className={cn(styles.slot, selected && styles.selected, styles.open)}
      onClick={handleClick}
      aria-pressed={selected}
      data-time={timeTextBasic}
    >
      <div className={cn(styles.time)}>{timeText}</div>
      <div className={cn(styles.total)}>Total {totalText}</div>
    </button>
  )
}
